import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { confirmDialog } from "../components/ConfirmDialog";
import Sidebar, { clearSession } from "../components/Sidebar";
import {
  Dashboard6Styles, ModalShell, InlineModal, Field, PagePreloader, Avatar, StatusChip, CardHead, PageHeader, KpiStrip, Pagination, ColumnPicker,
} from "../components/ModalUI";
import * as XLSX from "xlsx";

const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = PROD_API_BASE;
const SESSION_KEY = "zangon_session";

function safeJsonParse(t) {
  try { return JSON.parse(t); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, ms = 60000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  if (options.signal) {
    options.signal.addEventListener("abort", () => ctrl.abort());
  }
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } catch (e) {
    if (e?.name === "AbortError") throw new Error("Request aborted.");
    throw e;
  } finally {
    clearTimeout(id);
  }
}

async function refreshAccessToken() {
  const rt = getRefreshToken();
  if (!rt) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: rt }),
  });
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const method = (options.method || "GET").toUpperCase();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (method !== "GET" && method !== "HEAD") {
    if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }
  } else {
    delete headers["Content-Type"];
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const r = await refreshAccessToken();
      token = r.accessToken;
      headers["Authorization"] = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired.");
    }
  }
  return res;
}

function formatDateTime(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return String(iso || ""); }
}

function dayKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getCarTitle(car) {
  if (!car) return "";
  const mfr = (car.manufacturer || "").trim();
  const mk = (car.make || "").trim();
  const mdl = (car.model || "").trim();

  if (mfr && mk && mfr !== mk) return `${mfr} ${mk}`;
  if (mfr && mdl && mfr !== mdl) return `${mfr} ${mdl}`;
  if (mk && mdl && mk !== mdl) return `${mk} ${mdl}`;
  return mfr || mk || mdl || "";
}

function buildImaginUrl({ customer, make, model, year, color, angle = "01", zoomType = "fullscreen" }) {
  const base = "https://cdn.imagin.studio/getImage";
  const params = new URLSearchParams();
  params.set("customer", customer || "img");
  params.set("make", String(make || "").trim().toLowerCase());
  params.set("modelFamily", String(model || "").trim().toLowerCase());
  if (year) params.set("modelYear", String(year).trim());
  if (color) params.set("paintDescription", String(color || "").trim().toLowerCase());
  params.set("angle", angle);
  params.set("zoomtype", zoomType);
  return `${base}?${params.toString()}`;
}

const CAR_MAKES = [
  "Toyota", "Lexus", "Mercedes-Benz", "Honda", "Hyundai", "Acura", "Audi", "BMW", "BYD", "Cadillac",
  "Changan", "Chery", "Chevrolet", "Chrysler", "Citroen", "Daihatsu", "DFSK", "Dodge", "Dongfeng",
  "FAW", "Ferrari", "Fiat", "Ford", "GAC", "Geely", "Genesis", "GMC", "Great Wall", "Haima",
  "Haval", "Hino", "Hongqi", "Hummer", "Infiniti", "Isuzu", "Iveco", "IVM", "JAC", "Jaguar",
  "Jeep", "Jetour", "Jinbei", "JMC", "Kantanka", "Kia", "King Long", "Lamborghini", "Land Rover",
  "Lincoln", "Lucid", "Maserati", "Maxus", "Mazda", "MG", "Mini", "Mitsubishi", "NIO", "Nissan",
  "Nord", "Opel", "Peugeot", "Polestar", "Pontiac", "Porsche", "RAM", "Renault", "Rivian",
  "Rolls-Royce", "Rover", "Scion", "Skoda", "Subaru", "Suzuki", "Tesla", "Volkswagen", "Volvo",
  "XPeng", "Zeekr", "Other Make"
];

const CAR_COLORS = [
  "Black", "White", "Silver", "Gray", "Blue", "Red", "Green", "Gold", "Brown", "Beige",
  "Yellow", "Orange", "Purple", "Pink", "Other"
];

const CAR_YEARS = Array.from({ length: 38 }, (_, i) => 2027 - i);

const CAR_MODELS = {
  Toyota: ["Avalon", "Camry", "Corolla", "Fortuner", "Hiace", "Highlander", "Hilux", "Land Cruiser", "Prado", "RAV4", "Sienna", "Venza", "Yaris", "Sequoia", "Tacoma", "4Runner", "bZ4X"],
  Lexus: ["ES 250", "ES 300", "ES 350", "GX 460", "GX 470", "IS 250", "IS 300", "IS 350", "LX 570", "LX 600", "NX 300", "NX 350", "RX 330", "RX 350", "RX 500h", "RZ 450e"],
  "Mercedes-Benz": ["A-Class", "B-Class", "C-Class", "CLA", "CLS", "E-Class", "G-Class", "GLA", "GLK", "GLB", "GLC", "GLE", "GLS", "S-Class", "Maybach", "Sprinter", "EQA", "EQB", "EQE", "EQS"],
  Honda: ["Accord", "Civic", "CR-V", "HR-V", "Pilot", "Ridgeline", "Odyssey", "Fit", "City", "Passport", "Insight", "Crosstour", "Prologue", "e:Ny1"],
  Hyundai: ["Accent", "Elantra", "Sonata", "Tucson", "Santa Fe", "Palisade", "Creta", "Venue", "Kona", "Veloster", "Grand i10", "Azera", "Ioniq 5", "Ioniq 6", "Kona Electric"],
  Acura: ["ILX", "Integra", "MDX", "RDX", "TL", "TLX", "TSX", "ZDX", "NSX", "RLX"],
  BMW: ["1 Series", "2 Series", "3 Series", "4 Series", "5 Series", "7 Series", "X1", "X3", "X5", "X6", "X7", "M3", "M5", "i4", "iX", "i5", "i7", "iX3"],
  Audi: ["A3", "A4", "A5", "A6", "A7", "A8", "Q3", "Q5", "Q7", "Q8", "RS5", "RS7", "TT", "e-tron", "e-tron GT", "Q4 e-tron", "Q8 e-tron"],
  Ford: ["Edge", "Escape", "Explorer", "Everest", "F-150", "Ranger", "Transit", "Mustang", "Focus", "Fusion", "Bronco", "Expedition", "Mustang Mach-E", "F-150 Lightning"],
  Chevrolet: ["Camaro", "Corvette", "Cruze", "Malibu", "Impala", "Equinox", "Tahoe", "Suburban", "Silverado", "Traverse", "Trailblazer", "Bolt EV", "Equinox EV", "Blazer EV"],
  Nissan: ["Altima", "Maxima", "Sentra", "Sunny", "Micra", "Versa", "Rogue", "X-Trail", "Pathfinder", "Patrol", "Armada", "Frontier", "Leaf", "Ariya"],
  Kia: ["Rio", "Picanto", "Cerato", "Optima", "K5", "Sportage", "Sorento", "Telluride", "Seltos", "Carnival", "Stinger", "EV6", "EV9", "Niro EV"],
  Peugeot: ["208", "301", "308", "406", "407", "408", "508", "2008", "3008", "5008", "Landtrek"],
  Volkswagen: ["Golf", "Jetta", "Passat", "Polo", "Arteon", "Tiguan", "Touareg", "Atlas", "ID.4", "Beetle", "ID.3", "ID. Buzz"],
  Tesla: ["Model S", "Model 3", "Model X", "Model Y", "Cybertruck", "Roadster", "Semi", "Model 2", "Model C", "Model Q"],
  Mazda: ["Mazda2", "Mazda3", "Mazda6", "CX-3", "CX-5", "CX-7", "CX-9", "CX-30", "BT-50", "RX-8"],
  Mitsubishi: ["Lancer", "Mirage", "ASX", "Outlander", "Pajero", "Pajero Sport", "Eclipse Cross", "L200", "Montero", "Attrage"],
  Jeep: ["Wrangler", "Cherokee", "Grand Cherokee", "Compass", "Renegade", "Gladiator", "Commander", "Patriot", "Liberty", "Wagoneer"],
  "Land Rover": ["Defender", "Discovery", "Discovery Sport", "Range Rover", "Range Rover Sport", "Range Rover Velar", "Range Rover Evoque", "Freelander", "LR3", "LR4"],
  Porsche: ["911", "Cayenne", "Macan", "Panamera", "Taycan", "Boxster", "Cayman", "918 Spyder", "Carrera", "Turbo", "Macan Electric"],
  Volvo: ["S40", "S60", "S90", "V40", "V60", "V90", "XC40", "XC60", "XC90", "C30", "EX30", "EX90", "C40 Recharge"],
  Subaru: ["Impreza", "Legacy", "Outback", "Forester", "Ascent", "WRX", "BRZ", "XV", "Tribeca", "Baja"],
  Suzuki: ["Alto", "Swift", "Baleno", "Dzire", "Ertiga", "Vitara", "Grand Vitara", "Jimny", "Ciaz", "Ignis"],
  Maxus: ["T60", "T60 Max", "T70", "T90 EV", "eTerron 9", "D60", "D90", "G10", "Mifa 9"],
  Jaguar: ["XE", "XF", "F-Pace", "E-Pace", "I-Pace", "F-Type"],
  BYD: ["Atto 3", "Dolphin", "Seal", "Seagull", "Han", "Tang", "Song Plus", "Sealion 7"],
  MG: ["MG3", "MG5", "MG ZS", "MG ZS EV", "MG4 EV", "MG HS", "Cyberster"],
  Rivian: ["R1T", "R1S"],
  Lucid: ["Air", "Gravity"],
  Polestar: ["Polestar 2", "Polestar 3", "Polestar 4"],
  NIO: ["ET5", "ET7", "ES6", "ES8"],
  Zeekr: ["001", "007", "X"],
  XPeng: ["P7", "G6", "G9"],
  Cadillac: ["Escalade", "CT4", "CT5", "XT4", "XT5", "XT6", "Lyriq"],
  Changan: ["CS35 Plus", "CS55 Plus", "CS75 Plus", "CS85", "CS95", "UNI-K", "UNI-V"],
  Chery: ["Tiggo 2", "Tiggo 4 Pro", "Tiggo 7 Pro", "Tiggo 8 Pro", "Arrizo 8"],
  Chrysler: ["300", "Pacifica", "Voyager", "Town & Country"],
  Citroen: ["C3", "C4", "C5 Aircross", "Berlingo"],
  Daihatsu: ["Terios", "Sirion", "Mira", "Move", "Tanto"],
  Dodge: ["Charger", "Challenger", "Durango", "Hornet", "Journey"],
  Dongfeng: ["Nammi 01", "Aeolus Yixuan", "M-Hero 917", "Rich 6", "Forthing T5 EVO"],
  Ferrari: ["296 GTB", "SF90 Stradale", "F8 Tributo", "Roma", "Purosangue", "812 Superfast"],
  Fiat: ["500", "500X", "Panda", "Tipo", "Punto"],
  GAC: ["GS3", "GS4", "GS8", "GN8", "Empow"],
  Geely: ["Coolray", "Azkarra", "Monjaro", "Emgrand", "Tugella", "Galaxy L7", "Galaxy E8"],
  Genesis: ["G70", "G80", "G90", "GV70", "GV80", "GV60"],
  GMC: ["Sierra 1500", "Yukon", "Acadia", "Terrain", "Canyon", "Hummer EV"],
  "Great Wall": ["Poer", "Wingle 5", "Wingle 7", "Cannon"],
  Haval: ["H6", "Jolion", "Dargo", "H9"],
  Hongqi: ["E-HS9", "EH7", "EHS7", "H5", "H9", "HQ9", "HS5"],
  Hummer: ["H1", "H2", "H3", "H3T"],
  Infiniti: ["Q50", "Q60", "QX50", "QX55", "QX60", "QX80"],
  Isuzu: ["D-Max", "MU-X", "Trooper"],
  IVM: ["G5", "G40", "G80", "Fox", "Umu", "Connect", "Carrier"],
  JAC: ["JS4", "JS6", "JS8", "T6", "T8 Pro", "E30X", "e-J7"],
  Jetour: ["Dashing", "X70", "X90", "Traveller T2", "Shanhai T2", "Shanhai L9"],
  Lamborghini: ["Hurac\u00e1n", "Aventador", "Urus", "Revuelto", "Temerario"],
  Lincoln: ["Navigator", "Aviator", "Nautilus", "Corsair", "Continental"],
  Maserati: ["Ghibli", "Quattroporte", "Levante", "Grecale", "MC20"],
  Mini: ["Cooper", "Clubman", "Countryman", "Aceman"],
  Opel: ["Corsa", "Astra", "Mokka", "Crossland", "Grandland", "Insignia"],
  Pontiac: ["G6", "G8", "Grand Prix", "Firebird", "Vibe", "Solstice"],
  RAM: ["1500", "2500", "3500", "ProMaster", "Rampage"],
  Renault: ["Clio", "Megane", "Captur", "Kadjar", "Koleos", "Duster", "5 E-Tech"],
  "Rolls-Royce": ["Phantom", "Ghost", "Cullinan", "Wraith", "Dawn", "Spectre"],
  Rover: ["Mini", "25", "45", "75", "Streetwise", "SD1"],
  Scion: ["tC", "xB", "xD", "FR-S", "iA", "iM"],
  Skoda: ["Fabia", "Octavia", "Superb", "Kamiq", "Karoq", "Kodiaq", "Enyaq"],
  Nord: ["A3", "A5", "Tank", "Tusk", "Max"],
  Kantanka: ["Omama", "Onantefo", "Amoanimaa", "Opoku", "Mensah"],
  JMC: ["Vigus", "Yuhu", "Touring", "Carrying", "Boarding"],
  FAW: ["Bestune T77", "Bestune B70", "Bestune T99", "Sirius S80", "Jiefang J6"],
  Jinbei: ["Haise", "Grandview", "Granse", "F50"],
  DFSK: ["Glory 500", "Glory 580", "Glory iX5", "C31", "K01"],
  Haima: ["7X", "8S", "M3", "S5"],
  "King Long": ["Kingo", "Kaige", "Joylong", "XMQ6118"],
  Hino: ["Dutro", "300 Series", "500 Series", "700 Series", "Ranger"],
  Iveco: ["Daily", "Eurocargo", "S-Way", "Massif"]
};

function AddCarModal({ modalRef, estateId, ownersList, onSuccess, onClose }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    ownerId: "", ownerType: "", make: "", model: "", color: "", year: "", licensePlate: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const carPreviewUrl = useMemo(() => {
    if (!form.make || !form.model) return null;
    return buildImaginUrl({
      customer: "img",
      make: form.make,
      model: form.model,
      year: form.year || undefined,
      color: form.color || undefined,
    });
  }, [form.make, form.model, form.year, form.color]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOwners = useMemo(() => {
    if (!searchQuery) return ownersList;
    const q = searchQuery.toLowerCase();
    return ownersList.filter(o => {
      const nameMatch = `${o.firstName || ""} ${o.lastName || ""}`.toLowerCase().includes(q);
      const emailMatch = o.email && o.email.toLowerCase().includes(q);
      const phoneMatch = o.phoneNumber && o.phoneNumber.toLowerCase().includes(q);
      const bList = o.assignedBuildings || (o.assignedBuilding ? [o.assignedBuilding] : []);
      const houseMatch = bList.some(b => String(b.number || b.buildingNumber || "").toLowerCase().includes(q)) ||
        (o.currentBuildingNumber && String(o.currentBuildingNumber).toLowerCase().includes(q));
      return nameMatch || emailMatch || phoneMatch || houseMatch;
    });
  }, [ownersList, searchQuery]);

  const resetAndClose = () => {
    setForm({ ownerId: "", ownerType: "", make: "", model: "", color: "", year: "", licensePlate: "" });
    setSearchQuery("");
    setShowDropdown(false);
    setErrors({});
    setImageFile(null);
    setImagePreview(null);
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
    onClose?.();
  };

  const validate = () => {
    const e = {};
    if (!form.ownerId) e.ownerId = "Select an owner";
    if (!form.make) e.make = "Required";
    if (!form.model) e.model = "Required";
    if (!form.year) e.year = "Required";
    if (!form.licensePlate.trim()) e.licensePlate = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const postUrl = form.ownerType === "primary-occupant"
        ? `${API_BASE}/primary-occupants/${form.ownerId}/cars`
        : `${API_BASE}/estates/${estateId}/tenants/${form.ownerId}/cars`;

      const res = await authFetch(postUrl, {
        method: "POST",
        body: JSON.stringify({
          make: form.make,
          model: form.model,
          manufacturer: form.make,
          color: form.color,
          year: form.year ? parseInt(form.year) : undefined,
          licensePlate: form.licensePlate.trim(),
        }),
      });

      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to register car.");

      if (imageFile && json?.data) {
        try {
          const carId = typeof json.data === "string" ? json.data : json.data?.id;
          if (carId) {
            const fd = new FormData();
            fd.append("imageFile", imageFile);
            const putUrl = form.ownerType === "primary-occupant"
              ? `${API_BASE}/primary-occupants/${form.ownerId}/cars/${carId}`
              : `${API_BASE}/estates/${estateId}/tenants/${form.ownerId}/cars/${carId}`;
            await fetch(putUrl, {
              method: "PUT",
              headers: { Authorization: `Bearer ${getAccessToken()}` },
              body: fd,
            });
          }
        } catch { }
      }

      toast.success("Car registered successfully!");
      resetAndClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || "Failed to register car.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (key) => `d6m-input ${errors[key] ? "is-invalid" : ""}`;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-car"
      title="Register New Vehicle"
      onClose={resetAndClose}
      closeDisabled={loading}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={loading}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={loading}>
            {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Registering…</> : "Register Car"}
          </button>
        </>
      }
    >
      <div className="d6-card p-4 d-flex align-items-center justify-content-center bg-light border border-gray-200 mb-3 position-relative" style={{ height: 190 }}>
        <img
          alt="Car preview"
          src={imagePreview || carPreviewUrl || "/assets/media/preview-wait.png"}
          className="mw-100 mh-100 object-fit-contain"
          onError={e => { e.currentTarget.src = "/assets/media/preview-wait.png"; }}
        />
      </div>
      <div className="d-flex align-items-center justify-content-between gap-3 mb-5">
        <span className="d6m-muted">Preview updates as you pick a make and model.</span>
        <div className="d-flex gap-2">
          {imagePreview && (
            <button type="button" className="d6m-link-btn" onClick={() => { setImageFile(null); setImagePreview(null); }}>Remove photo</button>
          )}
          <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => fileInputRef.current?.click()}>
            {imagePreview ? "Change photo" : "Upload photo"}
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageSelect} />
      </div>

      <div ref={dropdownRef}>
        <Field label="Search owner" required error={errors.ownerId}>
          <div className="position-relative">
            <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400 pe-none"></i>
            <input
              type="text"
              className={`${inputCls("ownerId")} ps-10`}
              placeholder="Type name or email…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
                if (form.ownerId) setForm(p => ({ ...p, ownerId: "", ownerType: "" }));
              }}
              onFocus={() => setShowDropdown(true)}
            />
            {form.ownerId && (
              <i className="ki-outline ki-check-circle fs-4 text-success position-absolute top-50 end-0 translate-middle-y me-3"></i>
            )}
          </div>
          {showDropdown && (
            <div className="d6m-list mt-2" style={{ maxHeight: 220, overflowY: "auto" }}>
              {filteredOwners.length > 0 ? filteredOwners.map(o => {
                const oid = o.id;
                const ownerName = `${o.firstName || ""} ${o.lastName || ""}`.trim();
                return (
                  <button
                    type="button"
                    key={`${o._type}-${oid}`}
                    className="d6m-list-row"
                    onClick={() => {
                      setForm(prev => ({ ...prev, ownerId: oid, ownerType: o._type }));
                      setSearchQuery(ownerName);
                      setShowDropdown(false);
                      setErrors(prev => ({ ...prev, ownerId: null }));
                    }}
                  >
                    <Avatar src={o.imageUrl} name={ownerName} size={36} />
                    <span className="min-w-0 flex-grow-1 text-start">
                      <span className="d6m-v d-block text-truncate">{ownerName}</span>
                      <span className="d6m-muted d-block text-truncate">{o.email || o.phoneNumber || "No contact info"}</span>
                    </span>
                    <StatusChip color={o._type === "primary-occupant" ? "#16A34A" : "#2563EB"}>
                      {o._type === "primary-occupant" ? "Primary Occupant" : "Tenant"}
                    </StatusChip>
                  </button>
                );
              }) : (
                <div className="d6m-empty border-0">No owners found</div>
              )}
            </div>
          )}
        </Field>
      </div>

      <div className="d6m-section">
        <div className="d6m-section-head"><span>Vehicle information</span></div>
        <div className="row g-4">
          <Field className="col-6" label="Make" required error={errors.make}>
            <select className={inputCls("make")} value={form.make} onChange={e => setForm(p => ({ ...p, make: e.target.value, model: "" }))}>
              <option value="">Select make…</option>
              {CAR_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field className="col-6" label="Model" required error={errors.model}>
            <select className={inputCls("model")} value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} disabled={!form.make}>
              <option value="">Select model…</option>
              {(CAR_MODELS[form.make] || []).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field className="col-6" label="Year" required error={errors.year}>
            <select className={inputCls("year")} value={form.year} onChange={e => setForm(p => ({ ...p, year: e.target.value }))}>
              <option value="">Select year…</option>
              {CAR_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
          <Field className="col-6" label="Color">
            <select className="d6m-input" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))}>
              <option value="">Select color…</option>
              {CAR_COLORS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field className="col-12" label="License plate" required error={errors.licensePlate}>
            <input
              className={inputCls("licensePlate")}
              placeholder="e.g. ABC-123-XY"
              value={form.licensePlate}
              onChange={e => setForm(p => ({ ...p, licensePlate: e.target.value.toUpperCase() }))}
            />
          </Field>
        </div>
      </div>
    </ModalShell>
  );
}

const CARD_IMG_HEIGHT = 180;

function CarPreview({ car, small, plate }) {
  const IMAGIN_CUSTOMER = "img";
  const { manufacturer, make, model, year, color, imageUrl } = car || {};
  const carTitle = getCarTitle(car);

  const plateOverlay = plate && !small ? (
    <div
      className="position-absolute bottom-0 start-0 m-3 badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1"
      style={{ zIndex: 1 }}
    >
      {plate}
    </div>
  ) : null;

  if (!carTitle || carTitle === "Unknown") {
    return (
      <div
        className="position-relative rounded-3 bg-light d-flex align-items-center justify-content-center"
        style={small ? { width: 120, height: 80 } : { width: "100%", height: CARD_IMG_HEIGHT }}
      >
        <span className="text-gray-500 fw-semibold fs-7">No Image</span>
        {plateOverlay}
      </div>
    );
  }

  const imaginMake = manufacturer || (make !== model ? make : "") || "";
  const imaginModel = manufacturer ? (make || model) : (model || make || "");

  const url = imageUrl || buildImaginUrl({ customer: IMAGIN_CUSTOMER, make: imaginMake, model: imaginModel, year, color });

  return (
    <div className="d-flex flex-column" style={small ? { width: 120 } : { width: "100%" }}>
      <div
        className="position-relative rounded-3 bg-light overflow-hidden d-flex align-items-center justify-content-center border border-gray-200"
        style={small ? { width: 120, height: 80 } : { width: "100%", height: CARD_IMG_HEIGHT }}
      >
        <img
          src={url}
          alt={carTitle}
          style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", display: "block" }}
          onError={e => { e.currentTarget.style.display = "none"; }}
        />
        {plateOverlay}
      </div>
      <div className={`d-flex flex-wrap gap-2 ${small ?"mt-2" : "mt-3"}`}>
        <span className="badge bg-light text-gray-800 fw-semibold fs-9 rounded-pill px-2.5 py-1">{carTitle}</span>
        {year && <span className="badge bg-light text-gray-800 fw-semibold fs-9 rounded-pill px-2.5 py-1">{year}</span>}
        {color && <span className="badge bg-light-success text-success fw-semibold fs-9 rounded-pill px-2.5 py-1">{color}</span>}
      </div>
    </div>
  );
}

export default function CarManagement() {
  const session = getSession();
  const estateId = session?.estateId || "";

  const [pageReady, setPageReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rawCars, setRawCars] = useState([]);
  const [rawLogs] = useState([]);
  const [ownersList, setOwnersList] = useState([]);

  const [carSearch, setCarSearch] = useState("");
  const [filterMake, setFilterMake] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterColor, setFilterColor] = useState("");
  const [filterPhase, setFilterPhase] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 30;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [carDetails, setCarDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const addCarModalRef = useRef(null);
  const downloadCarModalRef = useRef(null);

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");

    const html = document.documentElement;
    const saved = localStorage.getItem("kt_theme_mode") || "light";
    html.setAttribute("data-bs-theme", saved);

    const onClick = (e) => {
      const el = e.target.closest('[data-kt-element="mode"]');
      if (!el) return;
      const mode = el.getAttribute("data-kt-value");
      if (mode) {
        if (window.KTThemeMode?.setMode) window.KTThemeMode.setMode(mode);
        else { html.setAttribute("data-bs-theme", mode); localStorage.setItem("kt_theme_mode", mode); }
      }
    };
    document.addEventListener("click", onClick);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };

    init();
    const t = setTimeout(init, 0);
    return () => { document.removeEventListener("click", onClick); clearTimeout(t); };
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("modal-open");
      document.body.style.overflow = "hidden";
    } else {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  const fetchData = useCallback(async () => {
    if (!estateId) {
      setIsLoading(false);
      setPageReady(true);
      return;
    }
    setIsLoading(true);
    try {
      const [primaryOccupantsRes, tenantsRes, carsRes] = await Promise.all([
        authFetch(`${API_BASE}/estates/${estateId}/primary-occupants?pageIndex=0&pageSize=1000`),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/with-dependents?pageIndex=0&pageSize=1000`),
        authFetch(`${API_BASE}/estates/${estateId}/cars?pageIndex=0&pageSize=1000`),
      ]);

      const combined = [];

      if (primaryOccupantsRes.ok) {
        const json = await primaryOccupantsRes.json();
        const items = Array.isArray(json?.data?.data)
          ? json.data.data
          : (Array.isArray(json?.data) ? json.data : []);
        items.forEach(o => combined.push({ ...o, _type: "primary-occupant" }));
      }

      if (tenantsRes.ok) {
        const json = await tenantsRes.json();
        const items = Array.isArray(json?.data?.data)
          ? json.data.data
          : (Array.isArray(json?.data) ? json.data : []);
        items.forEach(t => combined.push({ ...t, id: t.tenantId || t.id, _type: "tenant" }));
      }

      setOwnersList(combined);

      if (carsRes.ok) {
        const cJson = await carsRes.json();
        const cars = Array.isArray(cJson?.data?.data) ? cJson.data.data : [];
        setRawCars(cars);
      }
    } catch (err) {
      toast.error("Failed to load car data.");
      console.error(err);
    } finally {
      setIsLoading(false);
      setPageReady(true);
    }
  }, [estateId]);

  const fetchedEstateId = useRef(null);
  useEffect(() => {
    if (fetchedEstateId.current === estateId) return;
    fetchedEstateId.current = estateId;
    fetchData();
  }, [fetchData, estateId]);

  const allCars = useMemo(() => {
    return rawCars.map(c => {
      const formattedBuilding = c.ownerCurrentBuildings?.length > 0
        ? c.ownerCurrentBuildings.map(b => `${b.phaseName} - ${b.number}`).join(", ")
        : "Address pending update";

      return {
        id: c.id,
        licensePlate: c.licensePlate,
        car: {
          manufacturer: c.manufacturer || "",
          make: c.make || "",
          model: c.model || "",
          year: c.year,
          color: c.color,
          imageUrl: c.imageUrl
        },
        tenant: {
          id: c.ownerId,
          title: c.ownerTitle || "",
          fullName: c.ownerFullName || "Unknown Owner",
          building: formattedBuilding,
          phone: c.ownerPhone || null,
          avatarUrl: c.ownerImageUrl || null,
        },
        rawOwnerId: c.ownerId,
        rawPhaseList: c.ownerCurrentBuildings?.map(b => b.phaseName).filter(Boolean) || [],
        rawCarDetails: c,
      };
    });
  }, [rawCars]);

  const { inFeed, outFeed } = useMemo(() => {
    const inList = [];
    const outList = [];
    rawLogs.forEach(log => {
      const plate = log.vehiclePlate || log.plate || log.plateNumber || log.licensePlate;
      if (!plate) return;
      const matchedCar = allCars.find(c => c.licensePlate?.toLowerCase() === plate.toLowerCase());
      const carObj = matchedCar ? matchedCar.car : { manufacturer: "", make: "Unknown", model: "Vehicle", year: "", color: "" };
      const tenantObj = matchedCar ? matchedCar.tenant : { fullName: log.dependentName || log.checkedInByName || "Guest", building: "Visitor", phone: null };
      const driverObj = { name: log.checkedInByName || log.dependentName || "Driver", avatarUrl: null };
      if (log.checkedInAt) inList.push({ id: `${log.id}_in`, timestamp: log.checkedInAt, tenant: tenantObj, driver: driverObj, car: carObj, licensePlate: plate, direction: "IN" });
      if (log.checkedOutAt) outList.push({ id: `${log.id}_out`, timestamp: log.checkedOutAt, tenant: tenantObj, driver: driverObj, car: carObj, licensePlate: plate, direction: "OUT" });
    });
    inList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    outList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return { inFeed: inList, outFeed: outList };
  }, [rawLogs, allCars]);

  const stats = useMemo(() => {
    const today = dayKey(new Date());
    const todayIn = inFeed.filter(x => dayKey(new Date(x.timestamp)) === today).length;
    const todayOut = outFeed.filter(x => dayKey(new Date(x.timestamp)) === today).length;
    return { totalToday: todayIn + todayOut, todayIn, todayOut, totalCars: allCars.length };
  }, [inFeed, outFeed, allCars.length]);

  const { makeOptions, yearOptions, colorOptions, phaseOptions } = useMemo(() => {
    const phases = new Set();
    allCars.forEach(item => {
      item.rawPhaseList.forEach(p => phases.add(p));
    });
    return {
      makeOptions: Array.from(new Set(allCars.map(x => x.car.manufacturer || x.car.make).filter(Boolean))).sort(),
      yearOptions: Array.from(new Set(allCars.map(x => String(x.car.year || "")).filter(Boolean))).sort(),
      colorOptions: Array.from(new Set(allCars.map(x => x.car.color).filter(Boolean))).sort(),
      phaseOptions: Array.from(phases).sort(),
    };
  }, [allCars]);

  const filteredCars = useMemo(() => {
    setCurrentPage(1);
    const q = carSearch.trim().toLowerCase();
    return allCars.filter(item => {
      const manufacturer = String(item.car.manufacturer || "").toLowerCase();
      const make = String(item.car.make || "").toLowerCase();
      const model = String(item.car.model || "").toLowerCase();
      const color = String(item.car.color || "").toLowerCase();
      const plate = String(item.licensePlate || "").toLowerCase();
      const owner = String(item.tenant.fullName || "").toLowerCase();
      const building = String(item.tenant.building || "").toLowerCase();
      const carTitle = getCarTitle(item.car).toLowerCase();
      const rawBuildings = item.rawCarDetails?.ownerCurrentBuildings || [];
      const houseNumbers = rawBuildings.map(b => String(b.number || b.buildingNumber || "").toLowerCase());

      const matchesSearch = !q ||
        carTitle.includes(q) ||
        manufacturer.includes(q) ||
        make.includes(q) ||
        model.includes(q) ||
        color.includes(q) ||
        plate.includes(q) ||
        owner.includes(q) ||
        building.includes(q) ||
        houseNumbers.some(n => n.includes(q));

      const matchesMake = !filterMake || (item.car.manufacturer || item.car.make) === filterMake;
      const matchesYear = !filterYear || String(item.car.year) === String(filterYear);
      const matchesColor = !filterColor || item.car.color === filterColor;
      const matchesPhase = !filterPhase || item.rawPhaseList.includes(filterPhase);
      return matchesSearch && matchesMake && matchesYear && matchesColor && matchesPhase;
    });
  }, [allCars, carSearch, filterMake, filterYear, filterColor, filterPhase]);

  const totalPages = Math.max(1, Math.ceil(filteredCars.length / PAGE_SIZE));
  const pagedCars = filteredCars.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const openModal = async (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setIsLoadingDetails(true);

    const rawDetails = item.rawCarDetails || null;
    if (rawDetails?.assignedDependents) {
      setCarDetails(rawDetails);
      setIsLoadingDetails(false);
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${item.rawOwnerId}/cars/${item.id}`);
      const json = safeJsonParse(await res.text());
      if (res.ok && json?.data) {
        setCarDetails(json.data);
      } else {
        setCarDetails(rawDetails);
      }
    } catch {
      setCarDetails(rawDetails);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const closeModal = () => { setIsModalOpen(false); setSelectedItem(null); setCarDetails(null); };

  const [isDeletingCar, setIsDeletingCar] = useState(false);

  const handleDeleteCar = async (item) => {
    if (!estateId || !item?.id || !item.rawOwnerId) return toast.error("Car information is missing.");
    if (!(await confirmDialog({ title: "Remove Vehicle", message: `Remove ${item.licensePlate || "this vehicle"} from the estate? This cannot be undone.`, confirmLabel: "Remove Vehicle" }))) return;
    const rawType = String(item.rawCarDetails?.ownerType || "").toLowerCase().replace(/[^a-z]/g, "");
    const isPrimaryOccupant = rawType
      ? rawType.includes("primary") || rawType.includes("occupant")
      : ownersList.some(o => o._type === "primary-occupant" && o.id === item.rawOwnerId);
    const url = isPrimaryOccupant
      ? `${API_BASE}/primary-occupants/${item.rawOwnerId}/cars/${item.id}`
      : `${API_BASE}/estates/${estateId}/tenants/${item.rawOwnerId}/cars/${item.id}`;
    setIsDeletingCar(true);
    try {
      const res = await authFetch(url, { method: "DELETE" });
      if (!res.ok) {
        const json = safeJsonParse(await res.text());
        throw new Error(json?.message || "Failed to remove vehicle.");
      }
      setRawCars(prev => prev.filter(c => c.id !== item.id));
      toast.success("Vehicle removed.");
      closeModal();
    } catch (err) {
      toast.error(err.message || "Failed to remove vehicle.");
    } finally {
      setIsDeletingCar(false);
    }
  };

  const owner = selectedItem?.tenant;

  return (
    <>
      <Dashboard6Styles />

      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div
            id="kt_app_header"
            className="app-header"
            data-kt-sticky="true"
            data-kt-sticky-activate="{default: false, lg: true}"
            data-kt-sticky-name="app-header-sticky"
            data-kt-sticky-offset="{default: false, lg: '300px'}"
          >
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2"></i>
                </div>
                <Link to="/dashboard">
                  <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" />
                </Link>
              </div>
              <PageHeader title="Car Management">
                <button
                  type="button"
                  className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                  onClick={() => {
                    if (window.bootstrap?.Modal && downloadCarModalRef.current) {
                      window.bootstrap.Modal.getOrCreateInstance(downloadCarModalRef.current, { backdrop: "static" }).show();
                    }
                  }}
                >
                  <i className="ki-outline ki-file-down fs-6 me-sm-2"></i>
                  <span className="d-none d-sm-inline">Download Car Log</span>
                </button>
                <button
                  type="button"
                  className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                  onClick={() => {
                    if (window.bootstrap?.Modal && addCarModalRef.current) {
                      window.bootstrap.Modal.getOrCreateInstance(addCarModalRef.current, { backdrop: "static" }).show();
                    }
                  }}
                >
                  <i className="ki-outline ki-plus fs-6 me-sm-2 text-white"></i>
                  <span className="d-none d-sm-inline">Register Car</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">

                    <KpiStrip
                      loading={isLoading}
                      items={[
                        { icon: "ki-car", label: "Registered Vehicles", value: stats.totalCars, sub: "Resident vehicles" },
                        { icon: "ki-arrow-up-down", label: "Daily Traffic Movements", value: stats.totalToday, sub: "Daily movements" },
                        { icon: "ki-entrance-left", label: "Vehicle Entries", value: stats.todayIn, sub: "Entered today" },
                        { icon: "ki-entrance-right", label: "Vehicle Exits", value: stats.todayOut, sub: "Exited today" },
                      ]}
                    />

                    <div className="d6-card p-6 mb-5">
                      <CardHead title="All Registered Vehicles" subtitle="Search & filter vehicles in the estate" className="mb-5">
                        <div className="position-relative">
                          <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
                          <input
                            className="d6m-input ps-10"
                            style={{ width: 280, minHeight: 40, height: 40, borderRadius: 999 }}
                            placeholder="Plate, make, model, owner…"
                            value={carSearch}
                            onChange={(e) => setCarSearch(e.target.value)}
                          />
                        </div>
                        <select className="d6m-input" style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }} value={filterMake} onChange={(e) => setFilterMake(e.target.value)}>
                          <option value="">All Makes</option>
                          {makeOptions.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className="d6m-input" style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }} value={filterColor} onChange={(e) => setFilterColor(e.target.value)}>
                          <option value="">All Colors</option>
                          {colorOptions.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select className="d6m-input" style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }} value={filterPhase} onChange={(e) => setFilterPhase(e.target.value)}>
                          <option value="">All Phases</option>
                          {phaseOptions.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </CardHead>

                      <div>
                        {isLoading ? (
                          <div className="row g-5">
                            {[1, 2, 3, 4, 5, 6].map(n => (
                              <div key={n} className="col-12 col-md-6 col-xl-4">
                                <div className="d6-card h-100 p-5 placeholder-glow">
                                  <div className="placeholder rounded-3 w-100 mb-4" style={{ height: CARD_IMG_HEIGHT }}></div>
                                  <div className="d-flex align-items-center gap-3">
                                    <div className="placeholder rounded-circle w-45px h-45px"></div>
                                    <div className="flex-grow-1">
                                      <div className="placeholder col-8 h-20px rounded mb-2"></div>
                                      <div className="placeholder col-5 h-15px rounded"></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : filteredCars.length === 0 ? (
                          <div className="d6m-empty py-12">
                            <i className="ki-outline ki-car fs-3x text-gray-300 d-block mb-3"></i>
                            <div className="d6-title text-gray-900 mb-1">No cars found.</div>
                            <div className="fs-8 text-gray-500">Register a car to get started.</div>
                          </div>
                        ) : (
                          <>
                            <div className="row g-5">
                              {pagedCars.map((item) => (
                                <div key={item.id} className="col-12 col-md-6 col-xl-4">
                                  <button
                                    type="button"
                                    className="d6-card border-hover-success h-100 w-100 d-block cursor-pointer text-start transition-all p-5"
                                    onClick={() => openModal({ ...item, direction: "DETAILS" })}
                                  >
                                    <div className="mb-4">
                                      <CarPreview car={item.car} plate={item.licensePlate} />
                                    </div>
                                    <div className="p-3 bg-light rounded-3">
                                      <div className="d-flex align-items-center gap-3">
                                        <Avatar src={item?.tenant?.avatarUrl} name={item?.tenant?.fullName || "?"} size={40} />
                                        <div className="d-flex flex-column text-start overflow-hidden min-w-0">
                                          <span className="d6m-name fs-8 text-truncate">
                                            {item?.tenant?.title ? `${item.tenant.title} ` : ""}
                                            {item?.tenant?.fullName || "Unknown Owner"}
                                          </span>
                                          <span className="d6m-muted fs-9 text-truncate">{item?.tenant?.building || "—"}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                </div>
                              ))}
                            </div>

                            <Pagination
                              page={currentPage}
                              totalPages={totalPages}
                              onChange={setCurrentPage}
                              summary={`Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filteredCars.length)} of ${filteredCars.length} cars`}
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {/* Vehicle details */}
                    <InlineModal
                      open={isModalOpen && !!selectedItem}
                      icon="ki-car"
                      title="Vehicle Details"
                      onClose={closeModal}
                      width={720}
                      footer={
                        <div className="d-flex align-items-center justify-content-between w-100 gap-2">
                          <button type="button" className="btn btn-light-danger d6-mbtn" onClick={() => handleDeleteCar(selectedItem)} disabled={isDeletingCar}>
                            {isDeletingCar
                              ? <><span className="spinner-border spinner-border-sm me-2" />Removing…</>
                              : <><i className="ki-outline ki-trash fs-5 me-2" />Remove Vehicle</>}
                          </button>
                          <button type="button" className="btn btn-light d6-mbtn" onClick={closeModal} disabled={isDeletingCar}>Close</button>
                        </div>
                      }
                    >
                      {selectedItem && (
                        <>
                          <div className="d6-card p-4 d-flex align-items-center justify-content-center bg-light border border-gray-200 mb-5">
                            <div style={{ width: "100%", maxWidth: 420 }}>
                              <CarPreview car={selectedItem.car} plate={selectedItem.licensePlate} />
                            </div>
                          </div>

                          <div className="d6m-person">
                            <Avatar src={owner?.avatarUrl} name={owner?.fullName || "?"} size={56} />
                            <div className="min-w-0 flex-grow-1">
                              <div className="d6m-name text-truncate">
                                {owner?.title ? `${owner.title} ` : ""}
                                {owner?.fullName || "Unknown Owner"}
                              </div>
                              <div className="d6m-muted text-truncate">{owner?.building || "—"}</div>
                              {owner?.phone && <div className="d6m-muted">{owner.phone}</div>}
                            </div>
                            <StatusChip color="#16A34A">{selectedItem.licensePlate}</StatusChip>
                          </div>

                          <div className="d6m-section">
                            <div className="d6m-section-head">
                              <span>Authorised drivers</span>
                              {!isLoadingDetails && <span className="d6m-count">{carDetails?.assignedDependents?.length || 0}</span>}
                            </div>
                            {isLoadingDetails ? (
                              <div className="d-flex flex-column gap-2">
                                {[1, 2, 3].map(n => <span key={n} className="d6m-shimmer" style={{ height: 52, borderRadius: 12 }} />)}
                              </div>
                            ) : !carDetails?.assignedDependents || carDetails.assignedDependents.length === 0 ? (
                              <div className="d6m-empty">No authorised drivers registered for this vehicle.</div>
                            ) : (
                              <div className="d6m-list">
                                {carDetails.assignedDependents.map((dep, idx) => (
                                  <div key={dep.dependentId || idx} className="d6m-list-row" style={{ cursor: "default" }}>
                                    <Avatar src={dep.imageUrl} name={dep.fullName || "?"} size={38} />
                                    <span className="min-w-0 flex-grow-1 text-start">
                                      <span className="d6m-v d-block text-truncate">{dep.fullName}</span>
                                      <span className="d6m-muted d-block text-truncate">{dep.phoneNumber || "—"}</span>
                                    </span>
                                    <span className="d6m-muted flex-shrink-0">{dep.assignedAt ? formatDateTime(dep.assignedAt) : "—"}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {selectedItem.direction && selectedItem.direction !== "DETAILS" && (
                            <div className="d6m-section">
                              <div className="d6m-section-head"><span>Last activity</span></div>
                              <div className="d-flex align-items-center gap-3">
                                <StatusChip color={selectedItem.direction === "IN" ? "#16A34A" : "#DC2626"}>{selectedItem.direction}</StatusChip>
                                <span className="d6m-v">{formatDateTime(selectedItem.timestamp)}</span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </InlineModal>

                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-4">
                  <div className="text-gray-900 text-center fs-6">
                    <span className="text-gray-600 fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary fw-bold">Powered by Pixels Solutions Limited</a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <AddCarModal
        modalRef={addCarModalRef}
        estateId={estateId}
        ownersList={ownersList}
        onSuccess={fetchData}
        onClose={() => { }}
      />

      <DownloadCarModal modalRef={downloadCarModalRef} cars={filteredCars} />
    </>
  );
}

function DownloadCarModal({ modalRef, cars = [] }) {
  const [options, setOptions] = useState({
    licensePlate: true,
    manufacturer: true,
    make: true,
    model: true,
    year: true,
    color: true,
    ownerName: true,
    ownerPhone: true,
    building: true,
    phase: true,
    assignedDrivers: true,
  });

  const handleDownload = () => {
    const activeHeaders = Object.keys(options).filter(k => options[k]);
    if (activeHeaders.length === 0) {
      toast.error("Please select at least one column to download.");
      return;
    }

    if (cars.length === 0) {
      toast.error("No records to export.");
      return;
    }

    const headerMapping = {
      licensePlate: "License Plate",
      manufacturer: "Manufacturer",
      make: "Make",
      model: "Model",
      year: "Year",
      color: "Color",
      ownerName: "Owner Name",
      ownerPhone: "Owner Phone",
      building: "Building",
      phase: "Phase",
      assignedDrivers: "Assigned Drivers"
    };

    const formattedData = cars.map(item => {
      const row = {};
      activeHeaders.forEach(key => {
        const columnName = headerMapping[key];
        switch(key) {
          case 'licensePlate': row[columnName] = item.licensePlate || ''; break;
          case 'manufacturer': row[columnName] = item.car?.manufacturer || (item.car?.make !== item.car?.model ? item.car?.make : '') || ''; break;
          case 'make': row[columnName] = item.car?.make || item.car?.model || ''; break;
          case 'model': row[columnName] = item.car?.model || item.car?.make || ''; break;
          case 'year': row[columnName] = item.car?.year || ''; break;
          case 'color': row[columnName] = item.car?.color || ''; break;
          case 'ownerName': row[columnName] = item.tenant?.fullName || ''; break;
          case 'ownerPhone': row[columnName] = item.tenant?.phone || ''; break;
          case 'building': 
            if (item.rawCarDetails?.ownerCurrentBuildings?.length > 0) {
              row[columnName] = item.rawCarDetails.ownerCurrentBuildings.map(b => b.number).join(', ');
            } else {
              row[columnName] = item.tenant?.building || '';
            }
            break;
          case 'phase':
            row[columnName] = item.rawPhaseList?.join(', ') || '';
            break;
          case 'assignedDrivers':
            if (item.rawCarDetails?.assignedDependents) {
              row[columnName] = item.rawCarDetails.assignedDependents.map(d => d.dependentName || d.fullName || d.name).filter(Boolean).join(', ');
            } else {
              row[columnName] = 'None';
            }
            break;
          default: row[columnName] = '';
        }
      });
      return row;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(formattedData);
    XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
    XLSX.writeFile(wb, `vehicles_log_${new Date().toISOString().split("T")[0]}.xlsx`);

    toast.success("Download started successfully!");
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  const closeModal = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-down"
      title="Download Car Log"
      onClose={closeModal}
      width={640}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={closeModal}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleDownload}>
            <i className="ki-outline ki-file-down fs-5 me-2.5 text-white"></i> Download Export
          </button>
        </>
      }
    >
      <ColumnPicker options={options} setOptions={setOptions} note="Exports the vehicles that match your current filters." />
    </ModalShell>
  );
}
